import { t } from "../i18n"
// sync_controller.js — PWA offline sync & queue management
// Uses dedicated /pwa/ endpoints (session-authenticated, standalone mode only)
// Targets: indicator (dot), badge (count), lastSync (text), downloadBtn, syncStatus, conflictPanel

import { Controller } from '@hotwired/stimulus';
import { DirectUpload } from '@rails/activestorage';
import {
  storeTasks, storeAssets, storeLocations, storeCalendarEvents, storeParts,
  storeComplianceDocs, storePendingDocSignatures, getOfflineDocSignatures, markDocSignatureSynced,
  storeMembers, storeChecklistTemplates, storeComments,
  enqueue, getPendingQueue, removeFromQueue, getPendingCount,
  setMeta, getMeta, getStoreCounts, updateTaskLocal, getTask, removeTask,
  storeConflict, getConflicts, markConflictResolved, getConflictCount,
  getPendingCustodyEvents, markCustodyEventSynced,
  getPendingSignatures, markSignatureSynced,
  getPendingFiles, updateFileQueueEntry, removeFromFileQueue
} from '../offline_db';

const MEDIA_CACHE  = 'taagger-media-v1';
const PING_URL     = '/pwa/ping';
const CONNECTIVITY_INTERVAL = 30000; // 30s periodic check

export default class extends Controller {
  static values  = { companyId: Number }
  static targets = ['indicator', 'badge', 'lastSync', 'downloadBtn', 'syncStatus', 'conflictPanel', 'conflictCount']

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  async connect() {
    this._online   = navigator.onLine;
    this._deviceId = this._getOrCreateDeviceId();

    window.addEventListener('online',  this._handleOnline  = () => this._checkConnectivity());
    window.addEventListener('offline', this._handleOffline = () => this._wentOffline());
    window.addEventListener('taagger:queue-mutation', this._handleQueueEvent = (e) => this._onQueueEvent(e));

    // iOS has no Background Sync — drain queue whenever app is foregrounded and online.
    // This covers: was offline → closed app → came online → reopened app.
    document.addEventListener('visibilitychange', this._handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        this._checkConnectivity();
      }
    });

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', this._handleSwMessage = (e) => {
        if (e.data?.type === 'SYNC_COMPLETE') this._drainAll();
      });
    }

    window.TaaggerSync = this;

    this._detectPwaMode();

    // Verify actual connectivity on startup instead of trusting navigator.onLine
    await this._checkConnectivity();
    await this._refreshUI();

    // Periodic connectivity check — catches cases where navigator.onLine is wrong
    // (common on Mac Chrome PWA and some mobile browsers)
    this._connectivityTimer = setInterval(() => this._checkConnectivity(), CONNECTIVITY_INTERVAL);

    // Android: register Background Sync so queue replays even when app is closed.
    // iOS silently skips this (SyncManager not available), uses visibilitychange above.
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const reg = await navigator.serviceWorker.ready;
        await reg.sync.register('taagger-sync');
      } catch (e) { console.warn('[Sync] Background Sync registration failed:', e); }
    }
  }

  disconnect() {
    window.removeEventListener('online',  this._handleOnline);
    window.removeEventListener('offline', this._handleOffline);
    window.removeEventListener('taagger:queue-mutation', this._handleQueueEvent);
    document.removeEventListener('visibilitychange', this._handleVisibility);
    if (this._connectivityTimer) clearInterval(this._connectivityTimer);
    delete window.TaaggerSync;
  }

  // ── PWA standalone detection ─────────────────────────────────────────────────

  _detectPwaMode() {
    const isStandalone =
      window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches;

    if (isStandalone) document.body.classList.add('is-pwa');

    window.matchMedia('(display-mode: standalone)').addEventListener('change', (e) => {
      document.body.classList.toggle('is-pwa', e.matches);
    });
  }

  // ── Custom event handler ──────────────────────────────────────────────────

  async _onQueueEvent(event) {
    const { entityType, entityId, changeType, data, description } = event.detail || {};
    if (entityType && changeType) {
      await this.queueOfflineChange(entityType, entityId, changeType, data, description);
    }
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
  }

  // ── Device ID (stable per browser/device) ────────────────────────────────────

  _getOrCreateDeviceId() {
    let id = localStorage.getItem('taagger_device_id');
    if (!id) {
      id = 'pwa-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
      localStorage.setItem('taagger_device_id', id);
    }
    return id;
  }

  // ── Public: download all data ─────────────────────────────────────────────

  async download(event) {
    if (event?.preventDefault) event.preventDefault();
    if (!this._online) {
      this._showToast(t('javascript.controllers.sync_controller.offline_cannot_download'), 'error');
      return;
    }

    const days = event?.currentTarget?.dataset?.days || 30;
    this._setDownloadBtnLoading(true);

    try {
      const companyId = this.companyIdValue;
      const url = `/pwa/companies/${companyId}/sync?days=${days}&device_id=${encodeURIComponent(this._deviceId)}`;

      const resp = await fetch(url, {
        headers:     { 'Accept': 'application/json', 'X-Device-Id': this._deviceId },
        credentials: 'same-origin'
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const payload = await resp.json();
      const data    = payload.data || payload;

      await storeTasks(data.tasks           || []);
      await storeAssets(data.assets         || []);
      await storeLocations(data.locations   || []);
      await storeCalendarEvents(data.calendar_events || []);
      await storeParts(data.parts           || []);
      await storeComplianceDocs(data.compliance_docs       || []);
      await storePendingDocSignatures(data.pending_signatures || []);
      await storeMembers(data.members                      || []);
      await storeChecklistTemplates(data.checklist_templates || []);

      // Extract and store comments from task payloads
      const allComments = (data.tasks || []).flatMap(task =>
        (task.comments || []).map(c => ({ ...c, task_id: task.id }))
      );
      if (allComments.length) await storeComments(allComments);
      await setMeta('synced_at',  data.synced_at);
      await setMeta('company_id', data.company_id || companyId);
      await setMeta('days',       days);

      const mediaList = data.media || [];
      if (mediaList.length > 0) await this._downloadMedia(mediaList);

      await this._refreshUI();
      const counts = await getStoreCounts();
      this._showToast(
        t('javascript.controllers.sync_controller.downloaded_summary', { tasks: counts.tasks, assets: counts.assets, members: counts.members, files: mediaList.length }),
        'success'
      );
    } catch (err) {
      this._showToast(t('javascript.controllers.sync_controller.sync_failed', { error: err.message }), 'error');
    } finally {
      this._setDownloadBtnLoading(false);
    }
  }

  // ── Download media bytes directly from S3 to Cache API ───────────────────────

  async _downloadMedia(mediaList) {
    if (!('caches' in window)) return;

    const cache      = await caches.open(MEDIA_CACHE);
    const total      = mediaList.length;
    let   downloaded = 0;
    let   failed     = 0;
    const failedUrls = [];
    const totalBytes = mediaList.reduce((sum, m) => sum + (m.byte_size || 0), 0);

    if (totalBytes > 50 * 1024 * 1024) {
      const mb = Math.round(totalBytes / 1024 / 1024);
      const ok = confirm(t('javascript.controllers.sync_controller.confirm_download_mb', { mb }));
      if (!ok) return;
    }

    this._updateSyncStatus(t('javascript.controllers.sync_controller.downloading_files_progress', { current: 0, total }));

    for (const media of mediaList) {
      try {
        const response = await fetch(media.signed_url, { mode: 'cors' });
        if (response.ok) {
          await cache.put(`/pwa/media/${media.blob_key}`, response.clone());
          downloaded++;
        } else {
          failed++;
          failedUrls.push(media.signed_url);
          console.warn(`[Sync] Media download HTTP ${response.status} for blob_key=${media.blob_key}`);
        }
        this._updateSyncStatus(t('javascript.controllers.sync_controller.downloading_files_progress', { current: downloaded + failed, total }));
      } catch (e) {
        failed++;
        failedUrls.push(media.signed_url);
        console.error(`[Sync] Media download error for blob_key=${media.blob_key}:`, e);
      }
    }

    this._updateSyncStatus('');

    if (failed > 0) {
      console.warn(`[Sync] Failed media URLs:`, failedUrls);
      this._showToast(t('javascript.controllers.sync_controller.downloaded_partial', { downloaded, total, failed }), 'warning');
    }
  }

  // ── Public: queue an offline change ──────────────────────────────────────────
  // Captures the record's current updated_at so the server can detect conflicts.

  async queueOfflineChange(entityType, entityId, changeType, data, description) {
    // Capture current local record timestamp for server-side conflict detection
    let client_updated_at = data?.client_updated_at;
    if (!client_updated_at && entityType === 'task' && entityId) {
      const localRecord = await getTask(entityId);
      client_updated_at = localRecord?.updated_at;
    }

    const entry = {
      entityType,
      entityId,
      changeType,
      data: { ...data, client_updated_at, local_temp_id: data?.local_temp_id || (changeType === 'create' ? entityId : undefined) },
      description: description || `${changeType} ${entityType}`,
      local_changed_at: new Date().toISOString(),
      csrfToken: this._csrfToken()
    };

    // Deduplicate: if an identical change already exists in the queue, update it
    const pending = await getPendingQueue();
    const existing = pending.find(q =>
      q.entityType === entityType &&
      q.entityId === entityId &&
      q.changeType === changeType
    );

    let qid;
    if (existing) {
      await removeFromQueue(existing._qid);
      qid = await enqueue(entry);
      console.info(`[Sync] Deduplicated queue entry for ${entityType}#${entityId} (${changeType})`);
    } else {
      qid = await enqueue(entry);
    }

    await this._refreshUI();
    this._showToast(t('javascript.controllers.sync_controller.queued_for_sync', { description: entry.description }), 'warning');
    return qid;
  }

  // Legacy: raw HTTP queue (backward compat with offline-form controller)
  async queueMutation(url, method, body, description, csrfToken) {
    const qid = await enqueue({ url, method, body, description, csrfToken, _legacy: true });
    await this._refreshUI();
    this._showToast(t('javascript.controllers.sync_controller.queued_for_sync', { description }), 'warning');
    return qid;
  }

  // ── Public: optimistic task status update ─────────────────────────────────

  async offlineStatusChange(taskId, newStatus) {
    await updateTaskLocal(taskId, { status: newStatus });
    await this.queueOfflineChange('task', taskId, 'status_change', { status: newStatus }, t('javascript.controllers.sync_controller.update_task_status', { id: taskId, status: newStatus }));
  }

  // ── Online / offline handlers ─────────────────────────────────────────────

  // Active connectivity check — fetches a lightweight endpoint to confirm
  // actual server reachability. navigator.onLine is unreliable on many
  // platforms (Mac Chrome PWA, some mobile browsers report false negatives).
  async _checkConnectivity() {
    if (this._checkingConnectivity) return;
    this._checkingConnectivity = true;

    try {
      const resp = await fetch(PING_URL, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        signal: AbortSignal.timeout(5000)
      });
      const wasOffline = !this._online;
      this._online = resp.ok;
      // Broadcast real status so the offline banner stays in sync
      window.dispatchEvent(new CustomEvent('taagger:connectivity', { detail: { online: this._online } }));
      if (wasOffline && this._online) {
        await this._refreshUI();
        await this._drainAll();
        await this._deltaSync();
      } else if (!wasOffline && !this._online) {
        await this._refreshUI();
      }
    } catch {
      // Network failure — treat as offline only if navigator also says offline
      // (prevents false negatives from transient fetch failures while truly online)
      if (!navigator.onLine) {
        const wasOnline = this._online;
        this._online = false;
        window.dispatchEvent(new CustomEvent('taagger:connectivity', { detail: { online: false } }));
        if (wasOnline) await this._refreshUI();
      }
    } finally {
      this._checkingConnectivity = false;
    }
  }

  async _wentOnline() {
    this._online = true;
    await this._refreshUI();
    await this._drainAll();
    await this._deltaSync();
  }

  // ── Drain all pending queues ──────────────────────────────────────────────────

  async _drainAll() {
    await this._drainQueue();
    await this._drainCustodyEvents();
    await this._drainSignatures();
    await this._drainDocSignatures();
    await this._drainFiles();
    // Signal offline_upload_controller instances to drain their file queues
    window.dispatchEvent(new CustomEvent('taagger:files-drain'));
  }

  async _wentOffline() {
    this._online = false;
    await this._refreshUI();
    this._showToast(t('javascript.controllers.sync_controller.went_offline'), 'warning');
  }

  // ── Drain queue: push offline changes to server ───────────────────────────────
  // Strategy: latest_wins — server checks client_updated_at vs server updated_at.
  // If server is newer (conflict), the change is not applied and returned for user review.

  async _drainQueue() {
    const queue = await getPendingQueue();
    if (!queue.length) return;

    this._showToast(t('javascript.controllers.sync_controller.syncing_items', { count: queue.length }), 'info');

    const structured = queue.filter(q => !q._legacy);
    const legacy     = queue.filter(q => q._legacy);

    if (structured.length) {
      const companyId = this.companyIdValue;
      try {
        const changes = structured.map(q => ({
          entity_type:      q.entityType,
          entity_id:        q.entityId,
          change_type:      q.changeType,
          data:             q.data,
          client_updated_at: q.data?.client_updated_at || q.local_changed_at,
          local_changed_at: q.local_changed_at,
          client_change_id: String(q._qid)
        }));

        const resp = await fetch(`/pwa/companies/${companyId}/sync/batch`, {
          method:      'POST',
          headers:     {
            'Content-Type': 'application/json',
            'Accept':       'application/json',
            'X-CSRF-Token': this._csrfToken(),
            'X-Device-Id':  this._deviceId
          },
          credentials: 'same-origin',
          body:        JSON.stringify({ changes, conflict_strategy: 'latest_wins' })
        });

        if (resp.ok) {
          const result    = await resp.json();
          const applied   = result.data?.results?.applied   || [];
          const conflicts = result.data?.results?.conflicts || [];

          const appliedIds = new Set(applied.map(a => String(a.client_change_id)).filter(Boolean));

          for (const item of structured) {
            const itemId = String(item._qid);
            if (appliedIds.has(itemId)) await removeFromQueue(item._qid);
          }

          for (const item of applied) {
            if (item.entity_type === 'task' && item.task) {
              if (item.local_temp_id && String(item.local_temp_id) !== String(item.task.id)) {
                await removeTask(item.local_temp_id);
                await this._remapQueuedFiles(item.local_temp_id, item.task.id);
              }
              await storeTasks([item.task]);
            }
            if (item.entity_type === 'comment' && item.comment) {
              await storeComments([item.comment]);
            }
          }

          // Store conflicts in IndexedDB for user review
          if (conflicts.length) {
            for (const c of conflicts) await storeConflict(c);
            await this._refreshUI();
            this._showToast(
              t('javascript.controllers.sync_controller.conflicts_need_review', { count: conflicts.length }),
              'warning'
            );
          } else if (applied.length) {
            this._showToast(t('javascript.controllers.sync_controller.changes_synced', { count: applied.length }), 'success');
          }
        }
      } catch (err) {
        console.warn('[PWA Sync] Batch push failed:', err);
      }
    }

    // Replay legacy raw-HTTP entries
    for (const item of legacy) {
      try {
        const resp = await fetch(item.url, {
          method:      item.method,
          headers:     { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': item.csrfToken },
          credentials: 'same-origin',
          body:        item.body
        });
        if (resp.ok || resp.status === 422) await removeFromQueue(item._qid);
      } catch (e) {
        const isNetwork = e instanceof TypeError;
        if (isNetwork) {
          console.warn('[Sync] Legacy queue replay network error (will retry):', item.description);
        } else {
          console.error('[Sync] Legacy queue replay failed:', item.description, e);
        }
      }
    }

    await this._refreshUI();
  }

  // ── Delta sync after reconnect ────────────────────────────────────────────

  async _deltaSync() {
    const lastSync = await getMeta('synced_at');

    // If never synced before, perform a full download instead of delta
    if (!lastSync) {
      console.info('[Sync] No previous sync found — performing full download instead of delta.');
      return this.download();
    }

    const companyId = this.companyIdValue;
    try {
      const since = encodeURIComponent(lastSync);
      const resp  = await fetch(
        `/pwa/companies/${companyId}/sync/delta?since=${since}&device_id=${encodeURIComponent(this._deviceId)}`,
        { headers: { 'Accept': 'application/json', 'X-Device-Id': this._deviceId }, credentials: 'same-origin' }
      );
      if (!resp.ok) return;

      const payload = await resp.json();
      const data    = payload.data || payload;

      await storeTasks(data.tasks   || []);
      await storeAssets(data.assets || []);
      await storeComments(data.comments || []);
      await storeLocations(data.locations || []);
      await storeCalendarEvents(data.calendar_events || []);
      await storeParts(data.parts || []);
      await storeComplianceDocs(data.compliance_docs || []);
      await storePendingDocSignatures(data.pending_signatures || []);
      await storeMembers(data.members || []);
      await storeChecklistTemplates(data.checklist_templates || []);
      await setMeta('synced_at', data.synced_at || new Date().toISOString());
    } catch (e) {
      console.error('[Sync] Delta sync failed:', e);
    }
  }

  // ── Conflict resolution ───────────────────────────────────────────────────
  // Called by "Keep mine" / "Use server" buttons rendered in the conflict panel.

  async resolveConflict(event) {
    const btn       = event.currentTarget;
    const cid       = parseInt(btn.dataset.conflictCid, 10);
    const resolution = btn.dataset.resolution;  // 'client_wins' | 'server_wins'
    const entityType = btn.dataset.entityType;
    const entityId   = btn.dataset.entityId;
    const changeType = btn.dataset.changeType;
    let   clientData = {};
    try { clientData = JSON.parse(btn.dataset.clientData || '{}'); } catch (e) { console.warn('[Sync] Failed to parse clientData JSON:', e); }

    btn.disabled = true;
    btn.textContent = t('javascript.controllers.sync_controller.resolving');

    try {
      const companyId = this.companyIdValue;
      const resp = await fetch(`/pwa/companies/${companyId}/sync/resolve`, {
        method:      'POST',
        headers:     {
          'Content-Type': 'application/json',
          'Accept':       'application/json',
          'X-CSRF-Token': this._csrfToken(),
          'X-Device-Id':  this._deviceId
        },
        credentials: 'same-origin',
        body:        JSON.stringify({ entity_type: entityType, entity_id: entityId, resolution, change_type: changeType, client_data: clientData })
      });

      if (resp.ok) {
        await markConflictResolved(cid);
        // If server wins, update local IndexedDB with server data
        if (resolution === 'server_wins' && entityType === 'task') {
          const result = await resp.json();
          const serverData = result.data?.server_data;
          if (serverData) await storeTasks([serverData]);
        }
        btn.closest('[data-conflict-item]')?.remove();
        await this._refreshUI();
        this._showToast(t('javascript.controllers.sync_controller.conflict_resolved'), 'success');
      } else {
        btn.disabled    = false;
        btn.textContent = resolution === 'client_wins' ? t('javascript.controllers.sync_controller.keep_mine') : t('javascript.controllers.sync_controller.use_server');
        this._showToast(t('javascript.controllers.sync_controller.failed_to_resolve'), 'error');
      }
    } catch (e) {
      console.error('[Sync] Conflict resolution failed:', e);
      this._showToast(t('javascript.controllers.sync_controller.conflict_resolution_error'), 'error');
      btn.disabled    = false;
      btn.textContent = resolution === 'client_wins' ? t('javascript.controllers.sync_controller.keep_mine') : t('javascript.controllers.sync_controller.use_server');
    }
  }

  // Show/hide the conflict panel
  async showConflicts(event) {
    if (event?.preventDefault) event.preventDefault();
    if (!this.hasConflictPanelTarget) return;

    const conflicts = await getConflicts();
    const html      = this._renderConflictPanel(conflicts);

    this.conflictPanelTargets.forEach(panel => {
      const isVisible = !panel.classList.contains('hidden');
      if (isVisible) {
        panel.classList.add('hidden');
      } else {
        panel.innerHTML = html;
        panel.classList.remove('hidden');
      }
    });
  }

  _renderConflictPanel(conflicts) {
    if (!conflicts.length) {
      return `<p class="text-xs text-gray-500 px-2 py-1">${t('javascript.controllers.sync_controller.no_unresolved_conflicts')}</p>`;
    }

    return conflicts.map(c => {
      const serverName = c.server_data?.title || c.server_data?.name || `${c.entity_type} #${c.entity_id}`;
      const serverTs   = c.server_updated_at ? new Date(c.server_updated_at).toLocaleString() : '?';
      const clientTs   = c.client_updated_at ? new Date(c.client_updated_at).toLocaleString() : '?';
      const clientDataJson = JSON.stringify(c.client_data || {}).replace(/'/g, '&#39;');

      return `
        <div data-conflict-item class="border border-amber-200 rounded-lg p-3 mb-2 bg-amber-50 text-xs">
          <p class="font-semibold text-amber-800 mb-1 truncate">⚠ ${serverName}</p>
          <p class="text-gray-500 mb-0.5">${t('javascript.controllers.sync_controller.your_change')} <span class="font-mono">${clientTs}</span></p>
          <p class="text-gray-500 mb-2">${t('javascript.controllers.sync_controller.server_updated')} <span class="font-mono">${serverTs}</span></p>
          <div class="flex gap-2">
            <button
              data-action="click->sync#resolveConflict"
              data-conflict-cid="${c._cid}"
              data-resolution="client_wins"
              data-entity-type="${c.entity_type}"
              data-entity-id="${c.entity_id}"
              data-change-type="${c.change_type}"
              data-client-data='${clientDataJson}'
              class="flex-1 text-xs bg-primary text-white rounded-lg px-2 py-1.5 hover:bg-primary/90 transition-colors">
              ${t('javascript.controllers.sync_controller.keep_mine')}
            </button>
            <button
              data-action="click->sync#resolveConflict"
              data-conflict-cid="${c._cid}"
              data-resolution="server_wins"
              data-entity-type="${c.entity_type}"
              data-entity-id="${c.entity_id}"
              data-change-type="${c.change_type}"
              data-client-data='${clientDataJson}'
              class="flex-1 text-xs bg-gray-200 text-gray-700 rounded-lg px-2 py-1.5 hover:bg-gray-300 transition-colors">
              ${t('javascript.controllers.sync_controller.use_server')}
            </button>
          </div>
        </div>`;
    }).join('');
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  async _refreshUI() {
    const queueCount    = await getPendingCount();
    const conflictCount = await getConflictCount();
    const totalBadge    = queueCount + conflictCount;
    const syncedAt      = await getMeta('synced_at');

    const dotClass = 'sync-dot ' + (
      !this._online && queueCount > 0 ? 'amber' :
      !this._online                   ? 'red'   :
      conflictCount > 0               ? 'amber' : 'green'
    );

    this.indicatorTargets.forEach(el => {
      el.className = dotClass;
      el.title     = this._online ? t('javascript.controllers.sync_controller.online') : t('javascript.controllers.sync_controller.offline');
    });

    const badgeText = totalBadge > 0 ? String(totalBadge) : '';
    this.badgeTargets.forEach(el => {
      el.textContent = badgeText;
      el.classList.toggle('hidden', totalBadge === 0);
      if (conflictCount > 0) {
        el.title = t('javascript.controllers.sync_controller.pending_and_conflicts', { pending: queueCount, conflicts: t('javascript.controllers.sync_controller.conflict_count', { count: conflictCount }) });
        el.classList.remove('bg-amber-500');
        el.classList.add('bg-red-500');
      } else {
        el.classList.remove('bg-red-500');
        el.classList.add('bg-amber-500');
      }
    });

    const ccText = conflictCount > 0 ? t('javascript.controllers.sync_controller.conflict_count', { count: conflictCount }) : '';
    this.conflictCountTargets.forEach(el => {
      el.textContent = ccText;
      el.classList.toggle('hidden', conflictCount === 0);
    });

    const syncText = syncedAt
      ? t('javascript.controllers.sync_controller.synced_ago', { time: this._relativeTime(new Date(syncedAt)) })
      : t('javascript.controllers.sync_controller.not_yet_downloaded');
    this.lastSyncTargets.forEach(el => { el.textContent = syncText; });
  }

  _setDownloadBtnLoading(loading) {
    this.downloadBtnTargets.forEach(btn => {
      btn.disabled    = loading;
      btn.textContent = loading ? t('javascript.controllers.sync_controller.downloading') : t('javascript.controllers.sync_controller.download_for_offline');
    });
  }

  _updateSyncStatus(message) {
    this.syncStatusTargets.forEach(el => { el.textContent = message; });
  }

  _showToast(message, type = 'info') {
    const colors = {
      success: 'bg-green-600',
      error:   'bg-red-600',
      warning: 'bg-amber-500',
      info:    'bg-blue-600'
    };
    const toast = document.createElement('div');
    toast.className = `fixed bottom-4 right-4 z-50 ${colors[type] || colors.info} text-white text-sm font-medium px-4 py-3 rounded-xl shadow-lg max-w-sm`;
    toast.style.cssText = 'animation: slideInUp .2s ease;';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 4000);
  }

  _relativeTime(date) {
    const diff = Math.floor((Date.now() - date) / 1000);
    if (diff < 60)    return t('javascript.controllers.sync_controller.just_now');
    if (diff < 3600)  return t('javascript.controllers.sync_controller.minutes_ago', { count: Math.floor(diff / 60) });
    if (diff < 86400) return t('javascript.controllers.sync_controller.hours_ago', { count: Math.floor(diff / 3600) });
    return date.toLocaleDateString();
  }

  // ── Drain custody events (check-in/out) ───────────────────────────────────
  async _drainCustodyEvents() {
    const pending = await getPendingCustodyEvents();
    if (!pending.length) return;

    const companyId = this.companyIdValue;
    const csrf      = this._csrfToken();

    for (const ev of pending) {
      try {
        const resp = await fetch(`/pwa/companies/${companyId}/custody_events`, {
          method:      'POST',
          headers:     { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrf },
          credentials: 'same-origin',
          body:        JSON.stringify({
            asset_id:    ev.asset_id,
            status:      ev.status,
            latitude:    ev.latitude,
            longitude:   ev.longitude,
            occurred_at: ev.queued_at
          })
        });
        if (resp.ok || resp.status === 422) await markCustodyEventSynced(ev._ceid);
      } catch (e) { console.error('[Sync] Custody event sync failed for asset', ev.asset_id, e); }
    }
  }

  // ── Drain signatures (offline sign-off) ───────────────────────────────────
  async _drainSignatures() {
    const pending = await getPendingSignatures();
    if (!pending.length) return;

    const companyId = this.companyIdValue;
    const csrf      = this._csrfToken();

    for (const sig of pending) {
      try {
        const resp = await fetch(`/pwa/companies/${companyId}/tasks/${sig.task_id}/sign`, {
          method:      'POST',
          headers:     { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrf },
          credentials: 'same-origin',
          body:        JSON.stringify({ signature_data: sig.signature_data })
        });
        if (resp.ok || resp.status === 422) await markSignatureSynced(sig._sid);
      } catch (e) { console.error('[Sync] Signature sync failed for task', sig.task_id, e); }
    }
  }

  // ── Drain document signature sign-offs (compliance docs signed offline) ──────

  async _drainDocSignatures() {
    const pending = await getOfflineDocSignatures();
    if (!pending.length) return;

    const companyId = this.companyIdValue;
    const csrf      = this._csrfToken();

    for (const sig of pending) {
      try {
        const body = {
          signature_data:       sig._offline_signature_data,
          checklist_responses:  sig._offline_checklist_responses || [],
          attestation_text:     sig._offline_attestation
        };
        const resp = await fetch(`/pwa/companies/${companyId}/document_signatures/${sig.id}/sign`, {
          method:      'POST',
          headers:     { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrf },
          credentials: 'same-origin',
          body:        JSON.stringify(body)
        });
        if (resp.ok || resp.status === 422) await markDocSignatureSynced(sig.id);
      } catch (e) { console.error('[Sync] Doc signature sync failed for id', sig.id, e); }
    }
  }

  async _remapQueuedFiles(localTempId, serverId) {
    const pending = await getPendingFiles();
    for (const file of pending) {
      if (file.entity_type === 'task' && String(file.entity_id) === String(localTempId)) {
        await updateFileQueueEntry(file._fqid, { entity_id: serverId });
      }
    }
  }

  async _drainFiles() {
    const pending = await getPendingFiles();
    if (!pending.length) return;

    const companyId = this.companyIdValue;
    const directUploadUrl = document.querySelector("meta[name='direct-upload-url']")?.content
      || '/rails/active_storage/direct_uploads';
    const csrfToken = this._csrfToken();

    for (const entry of pending) {
      if (String(entry.entity_id || '').startsWith('offline-')) continue;

      try {
        await updateFileQueueEntry(entry._fqid, { status: 'uploading' });
        const file = new File([entry.data], entry.filename, { type: entry.content_type || 'application/octet-stream' });
        const signedId = await this._directUpload(file, directUploadUrl);

        const resp = await fetch(`/pwa/companies/${entry.company_id || companyId}/attachments`, {
          method:      'POST',
          headers:     { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
          credentials: 'same-origin',
          body:        JSON.stringify({
            entity_type:     entry.entity_type,
            entity_id:       entry.entity_id,
            attachment_type: entry.attachment_type,
            signed_blob_ids: [signedId]
          })
        });

        if (resp.ok) {
          await removeFromFileQueue(entry._fqid);
        } else {
          await updateFileQueueEntry(entry._fqid, { status: 'failed', retries: (entry.retries || 0) + 1 });
        }
      } catch (e) {
        await updateFileQueueEntry(entry._fqid, { status: 'failed', retries: (entry.retries || 0) + 1 });
        console.error('[Sync] File queue upload failed:', entry.filename, e);
      }
    }
  }

  _directUpload(file, url) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, url);
      upload.create((error, blob) => {
        if (error) reject(error);
        else resolve(blob.signed_id);
      });
    });
  }
}
